import React, { useMemo } from 'react';
import { useDocs } from '../../../../../../hooks/useDocs';
import { useAuth } from '../../../../../AuthProvider';
import { isAdminOnlyDoc } from '../../../../../../utils/docTags';
import {
  resolveAttachedDocTitle,
  resolveAttachedDocIcon,
  isAttachedDocPermanentlyGone,
  isAttachedDocNamePending,
  formatPermanentlyDeletedLabel,
} from '../../../../../../utils/attachedDocUtils';
import AttachedDocMark from '../../../../FlowchartView/components/TaskDetailSidebar/components/AttachedDocsSection/components/AttachedDocMark';

/**
 * Compact list of docs attached to a mission, shown on the card when the
 * Attached Docs field is enabled.
 */
const AttachedDocsOnCard = ({ task, onOpenDoc }) => {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';
  const { allDocs, loading: libraryLoading } = useDocs({ eager: true });

  const docsById = useMemo(() => {
    const map = new Map();
    for (const doc of allDocs) map.set(doc.id, doc);
    return map;
  }, [allDocs]);

  const visibleAttached = useMemo(() => {
    const attachedDocs = Array.isArray(task?.attachedDocs) ? task.attachedDocs : [];
    return attachedDocs.filter((entry) => {
      if (!entry?.docId) return false;
      const meta = docsById.get(entry.docId);
      if (!meta) return true;
      return canViewAdminDocs || !isAdminOnlyDoc(meta);
    });
  }, [task?.attachedDocs, docsById, canViewAdminDocs]);

  if (visibleAttached.length === 0) return null;

  return (
    <div className="mission-card__attached-docs">
      {visibleAttached.map((entry) => {
        const meta = docsById.get(entry.docId);
        const loadingMeta = isAttachedDocNamePending(entry, meta, libraryLoading);
        const inBin = !!meta?.deletedAt;
        const permanentlyGone = isAttachedDocPermanentlyGone(entry, meta, libraryLoading);
        const rowTitle = permanentlyGone
          ? formatPermanentlyDeletedLabel(entry, meta)
          : resolveAttachedDocTitle(entry, meta);
        const rowIcon = resolveAttachedDocIcon(entry, meta);
        const canOpen = !loadingMeta && !permanentlyGone && !inBin && !!onOpenDoc;
        return (
          <button
            key={entry.docId}
            type="button"
            className={`mission-card__attached-doc${
              permanentlyGone ? ' mission-card__attached-doc--gone' : ''
            }${inBin ? ' mission-card__attached-doc--in-bin' : ''}`}
            disabled={!canOpen}
            title={
              loadingMeta
                ? 'Loading doc…'
                : inBin
                  ? `${rowTitle} is in the bin`
                  : permanentlyGone
                    ? 'This doc was permanently deleted'
                    : `Open ${rowTitle}`
            }
            onClick={(e) => {
              e.stopPropagation();
              if (canOpen) onOpenDoc(entry.docId);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <span className="mission-card__attached-doc-icon" aria-hidden="true">
              {loadingMeta ? null : <AttachedDocMark icon={rowIcon} size={16} />}
            </span>
            <span className="mission-card__attached-doc-title">
              {loadingMeta ? 'Loading doc…' : rowTitle}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default AttachedDocsOnCard;
